import { _ } from '/@/locale';

export interface CropOption {
    label: string;
    value: number;
}

export interface CampaignCrop {
    crop: string;
    key?: number | string;
    yield: number | string;
    options: CropOption[];
}

export interface Campaign {
    season: number | string;
    crops: CampaignCrop[];
}

export type SelectedCampaigns = Record<string, Campaign>;

export interface SeasonResult {
    season: number | string;
    co: { simulated: number; fixed: number };
}

export const MAX_CROPS_PER_CAMPAIGN = 3;

/** The first result is the initial carbon, which the API returns as season 0. */
export const seasonLabel = (season: number | string) =>
    Number(season) ? `${season}/${Number(season) + 1}` : _('Carbono Inicial');

export function round(value: number, digits: number) {
    const factor = 10 ** digits;
    const nudged = value + Math.sign(value) * Number.EPSILON;
    return Math.round(nudged * factor) / factor;
}

// Valida que exista al menos un crop con data valida cargado en alguna de las campañas
export function hasValidCrop(selectedCampaigns: SelectedCampaigns) {
    return Object.values(selectedCampaigns).some((campaign) =>
        campaign.crops.some((crop) => !!crop.crop && !!crop.yield)
    );
}

export function buildRequestBody(selectedCampaigns: SelectedCampaigns) {
    const campaigns = Object.entries(selectedCampaigns)
        .map(([key, campaign]) => {
            return {
                season: key,
                crops: campaign.crops.filter((crop) => Boolean(crop.crop) && Boolean(crop.yield)), // Solo manda los crops con data completa
            };
        })
        .filter((campaign) => campaign.crops.length > 0);

    return { campaigns };
}

// Campaign crops -------------------------------------------------------------------------
// A campaign holds up to 3 crops and a crop can't repeat within it, so each crop's options
// are the previous crop's options minus the previous crop. These helpers never mutate.

const optionsAfter = (crop: CampaignCrop): CropOption[] => crop.options.filter((o) => o.value !== crop.key);

const clearCrop = (crop: CampaignCrop): CampaignCrop => ({ ...crop, crop: '', key: '', yield: '' });

/** Re-derives every crop's options from the one before it; the first gets `firstOptions`. */
function chainOptions(crops: CampaignCrop[], firstOptions?: CropOption[]): CampaignCrop[] {
    const chained: CampaignCrop[] = [];
    crops.forEach((crop, index) => {
        const previous = chained[index - 1];
        const options = previous ? optionsAfter(previous) : (firstOptions ?? crop.options);
        chained.push({ ...crop, options });
    });
    return chained;
}

export function setCropYield(campaign: Campaign, index: number, value: number | string): Campaign {
    return {
        ...campaign,
        crops: campaign.crops.map((crop, i) => (i === index ? { ...crop, yield: value } : crop)),
    };
}

/** Picks a crop; any later crop that now repeats it is cleared. */
export function setCrop(campaign: Campaign, index: number, option: CropOption): Campaign {
    const crops = campaign.crops.map((crop, i) => {
        if (i === index) return { ...crop, crop: option.label, key: option.value };
        if (i > index && crop.key === option.value) return clearCrop(crop);
        return crop;
    });
    return { ...campaign, crops: chainOptions(crops) };
}

export function addCrop(campaign: Campaign): Campaign {
    const last = campaign.crops[campaign.crops.length - 1];
    if (!last || campaign.crops.length >= MAX_CROPS_PER_CAMPAIGN) return campaign;
    return { ...campaign, crops: [...campaign.crops, { crop: '', yield: '', options: optionsAfter(last) }] };
}

export function removeCrop(campaign: Campaign, index: number, allCrops: CropOption[]): Campaign {
    const remaining = campaign.crops.filter((_crop, i) => i !== index);
    return { ...campaign, crops: chainOptions(remaining, allCrops) };
}

/** The campaigns from `from` to `to`, keeping the crops of the ones already loaded. */
export function campaignsInRange(
    selectedCampaigns: SelectedCampaigns,
    from: number,
    to: number,
    allCrops: CropOption[]
): SelectedCampaigns {
    const campaigns: SelectedCampaigns = {};
    for (let season = from; season <= to; season++) {
        campaigns[season] = selectedCampaigns[season] ?? {
            season,
            crops: [{ crop: '', yield: '', options: allCrops }],
        };
    }
    return campaigns;
}

// Inputs -------------------------------------------------------------------------------------

/** Accepted range per input: greater than `min`, up to `max`. */
export const INPUT_RANGES = {
    temperature: { min: 0, max: 40 },
    clay: { min: 0, max: 100 },
    organicMaterial: { min: 0, max: 100 },
    density: { min: 0, max: 3 },
} as const;

export type InputName = keyof typeof INPUT_RANGES;

export type InputError = 'required' | 'range' | null;

export function validateInput(name: InputName, value: unknown): InputError {
    if (value === undefined || value === null || value === '') return 'required';
    const number = Number(value);
    const { min, max } = INPUT_RANGES[name];
    if (!Number.isFinite(number) || number <= min || number > max) return 'range';
    return null;
}

export interface CalculatorInputs {
    temperature: unknown;
    clay: unknown;
    organicMaterial: unknown;
    density: unknown;
}

export const areInputsValid = (inputs: CalculatorInputs) =>
    (Object.keys(INPUT_RANGES) as InputName[]).every((name) => validateInput(name, inputs[name]) === null);

// Charts -------------------------------------------------------------------------------------

/** 1, 2 or 5 times a power of ten, so axis ticks land on round numbers. */
export function niceStep(span: number, targetTicks = 4) {
    const raw = span / targetTicks;
    if (!(raw > 0)) return 1;
    const magnitude = 10 ** Math.floor(Math.log10(raw));
    const normalized = raw / magnitude;
    const factor = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
    return factor * magnitude;
}

export interface AxisScale {
    domain: [number, number];
    ticks: number[];
    decimals: number;
}

function scaleBetween(low: number, high: number): AxisScale {
    const step = niceStep(high - low);
    const decimals = Math.max(0, -Math.floor(Math.log10(step)));
    const start = Math.floor(low / step);
    const end = Math.ceil(high / step);
    const ticks: number[] = [];
    for (let i = start; i <= end; i++) ticks.push(round(i * step, decimals));
    return { domain: [round(start * step, decimals), round(end * step, decimals)], ticks, decimals };
}

/** Soil carbon: some room around the values so small changes don't read as big swings. */
export function soilCarbonScale(values: number[]): AxisScale {
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = Math.max((max - min) * 0.25, Math.abs((max + min) / 2) * 0.05, 0.5);
    return scaleBetween(min - padding, max + padding);
}

/** CO2 balance: symmetric around zero so gains and losses compare at a glance. */
export function co2BalanceScale(values: number[]): AxisScale {
    const extent = Math.max(...values.map(Math.abs), 1) * 1.1;
    return scaleBetween(-extent, extent);
}
